import React, { useState } from 'react';
import { alpha, Box, Button, Divider, IconButton, ListItemIcon, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import LabelOffOutlinedIcon from '@mui/icons-material/LabelOffOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import PlaylistRemoveIcon from '@mui/icons-material/PlaylistRemove';

import { CrmContactTotal } from '../contactList';
import { ACTION_LABELS, BulkAction, MAX_TICKED, people } from '../../tagging/tagging';
import { pageState, Selection } from './selection';

interface BulkActionBarProps {
  selection: Selection;
  pageIds: ReadonlyArray<number>;
  /** Everyone the filter matches: exact, or the list's estimate. */
  total: CrmContactTotal | undefined;
  /** The last tick was refused: MAX_TICKED are ticked already. */
  capped: boolean;
  onSelectAll: () => void;
  onClear: () => void;
  onAction: (action: BulkAction) => void;
  /** NationBuilder's batch update (card 20): fields, consent, tags, lists, a logged contact, memberships. */
  onBatchUpdate: () => void;
}

const n = (value: number) => value.toLocaleString('en-US');

const matching = (total: CrmContactTotal) => (total.exact ? n(total.count) : `about ${n(Math.round(total.count / 100) * 100)}`);

/**
 * Shown above the table while anything is selected: how many, the way to
 * select everyone the filter matches, and what can be done to them.
 */
export const BulkActionBar = ({ selection, pageIds, total, capped, onSelectAll, onClear, onAction, onBatchUpdate }: BulkActionBarProps) => {
  const [menu, setMenu] = useState<HTMLElement | null>(null);

  if (selection.kind === 'none') return null;

  const wholePage = pageState(selection, pageIds) === 'all';
  const more = selection.kind === 'ids' && wholePage && total !== undefined && total.count > selection.ids.length;

  const said =
    selection.kind === 'all'
      ? total
        ? `All ${matching(total)} contacts that match are selected`
        : 'Everyone that matches is selected'
      : selection.ids.length === pageIds.length && wholePage
        ? `All ${people(selection.ids.length)} on this page are selected`
        : `${n(selection.ids.length)} selected`;

  const act = (action: BulkAction) => {
    setMenu(null);
    onAction(action);
  };

  return (
    <Box
      role="region"
      aria-label="Selection"
      sx={(theme) => ({
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        px: { xs: 1.5, sm: 2 },
        py: 1,
        bgcolor: alpha(theme.palette.primary.main, 0.06),
        borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
        borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
      })}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, flex: '1 1 260px', minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }} aria-live="polite">
          {said}
        </Typography>
        {more && (
          <Button size="small" onClick={onSelectAll} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Select all {matching(total!)} that match
          </Button>
        )}
        <Button size="small" color="inherit" onClick={onClear} sx={{ textTransform: 'none', color: 'text.secondary' }}>
          Clear selection
        </Button>
        {capped && (
          <Typography variant="caption" color="warning.dark" component="p" sx={{ flexBasis: '100%' }}>
            At most {n(MAX_TICKED)} can be ticked one by one. To change more, select all that match.
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
        <Button size="small" variant="contained" disableElevation startIcon={<EditNoteOutlinedIcon />} onClick={onBatchUpdate}>
          Batch update
        </Button>
        <Button size="small" variant="outlined" startIcon={<LocalOfferOutlinedIcon />} onClick={() => act('addTag')}>
          {ACTION_LABELS.addTag}
        </Button>
        <Button size="small" variant="outlined" startIcon={<PlaylistAddIcon />} onClick={() => act('addList')}>
          {ACTION_LABELS.addList}
        </Button>
        <Tooltip title="Remove a tag or take off a list">
          <IconButton
            size="small"
            aria-label="More actions"
            aria-haspopup="menu"
            aria-expanded={Boolean(menu)}
            onClick={(event) => setMenu(event.currentTarget)}
          >
            <MoreHorizIcon />
          </IconButton>
        </Tooltip>
        <Menu anchorEl={menu} open={Boolean(menu)} onClose={() => setMenu(null)}>
          <MenuItem onClick={() => act('removeTag')}>
            <ListItemIcon>
              <LabelOffOutlinedIcon fontSize="small" />
            </ListItemIcon>
            {ACTION_LABELS.removeTag}
          </MenuItem>
          <Divider />
          <MenuItem onClick={() => act('removeList')}>
            <ListItemIcon>
              <PlaylistRemoveIcon fontSize="small" />
            </ListItemIcon>
            {ACTION_LABELS.removeList}
          </MenuItem>
        </Menu>
      </Box>
    </Box>
  );
};
